import React, { useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { AUDIT_PLAN_ALLOWED_TYPES } from "../../utils/auditPlanFileUtils";

/**
 * Same centered card layout as PdfViewer errorNode ("Couldn't load this document" / Try again).
 */
const AuditPlanUploadZone = ({ onFileSelect, disabled = false }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState(false);

  const customColors = theme.customColors || {};
  const borderMain = customColors.border?.main ?? "#E8EAED";
  const textSecondary = customColors.text?.secondary ?? "#6B7280";

  const handleFile = (file) => {
    if (!file || disabled) return;
    const isAllowed = AUDIT_PLAN_ALLOWED_TYPES.some((ext) =>
      file.name?.toLowerCase().endsWith(ext)
    );
    if (!isAllowed) {
      setFileError(true);
      return;
    }
    setFileError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onFileSelect?.(file);
  };

  return (
    <Box
      role="region"
      aria-label="Upload audit plan"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 400,
        p: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: customColors.background?.tableRow ?? "#F3F4F6",
          boxShadow: customColors.shadow?.card ?? "0 1px 3px rgba(0,0,0,0.1)",
          border: `1px solid ${borderMain}`,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 220,
          maxWidth: 520,
          width: "100%",
          px: 3,
          py: 4,
          gap: 2,
        }}
      >
        <CloudUploadIcon
          sx={{
            color: fileError ? "#C71414" : (customColors.icons?.secondary ?? textSecondary),
            fontSize: 40,
            mb: 0.5,
          }}
          aria-hidden
        />
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          No audit plan uploaded
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", maxWidth: 360 }}>
          Upload an audit plan document to preview it here. Allowed file types:{" "}
          {AUDIT_PLAN_ALLOWED_TYPES.join(", ")}.
        </Typography>
        {fileError && (
          <Typography variant="caption" sx={{ color: "#C71414", textAlign: "center" }}>
            Invalid file type. Please use {AUDIT_PLAN_ALLOWED_TYPES.join(" or ")}.
          </Typography>
        )}
        <LoadingButton
          variant="contained"
          color="primary"
          loading={disabled}
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Upload audit plan document"
          sx={{
            mt: 1,
            textTransform: "none",
            px: 3,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors?.button?.hover,
            },
          }}
        >
          Upload document
        </LoadingButton>
        <input
          type="file"
          accept={AUDIT_PLAN_ALLOWED_TYPES.join(",")}
          hidden
          ref={fileInputRef}
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
};

export default AuditPlanUploadZone;
